import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { useFormContext } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { UploadIcon, XIcon, ImageIcon } from 'lucide-react'
import {
  FileUpload,
  FileUploadDropzone,
  FileUploadItem,
  FileUploadItemDelete,
  FileUploadItemMetadata,
  FileUploadItemPreview,
  FileUploadList,
} from '@/components/ui/file-upload'
import { MinimalTiptapEditor } from '@/components/ui/minimal-tiptap/minimal-tiptap'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import TextField from 'app/frontend/base/components/form-fields/TextField'
import FieldWrapper from 'app/frontend/base/components/form-fields/FieldWrapper'
import { FormField, FormGroup } from '@/components/ui/form'
import { FormEntityContext } from 'app/frontend/base/config/context/forms'
import { useContext, useState } from 'react'
import ProtectedContent from 'app/frontend/base/components/ProtectedContent'
import Select from 'app/frontend/base/components/Select'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { cn } from '@/lib/utils'
import Card from 'app/frontend/base/components/Card'
import { NewsParentEntityType, NewsPermission } from './types'
import { NEWS_TAGS } from 'app/frontend/base/pages/News/tags'
import NewsTagBadge from 'app/frontend/base/pages/News/NewsTagBadge'

const formSchema = z.object({
  title: z.string().min(1),
  content: z.string().min(1),
  published: z.boolean(),
  mainImageUrl: z.string().nullable().optional(),
  target: z.enum(['team', 'guild']).optional(),
  tags: z.array(z.string()).optional(),
})

type NewsFormItem = Partial<wowaudit.NewsItem> & {
  target?: 'team' | 'guild'
  tags?: string[]
}

const NewsFormContent = ({
  uploadFile,
  showTargetSelector,
  showTags,
}: {
  uploadFile: (file: File) => Promise<string>
  showTargetSelector: boolean
  showTags: boolean
}) => {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext()
  const { entityType, i18nPrefix } = useContext(FormEntityContext)
  const [imageTab, setImageTab] = useState<'url' | 'upload'>('url')
  const [imageFiles, setImageFiles] = useState<File[]>([])
  const [rejection, setRejection] = useState<{
    fileName: string
    message: string
  } | null>(null)

  const handleMainImageUpload = async (files: File[]) => {
    setImageFiles(files)
    if (files.length > 0) {
      const url = await uploadFile(files[0])
      setValue('mainImageUrl', url, { shouldDirty: true })
    }
  }

  return (
    <div className='space-y-6'>
      <FormGroup>
        <TextField name='title' className='flex-1' layout='stacked' />
        {showTargetSelector && (
          <ProtectedContent
            section='base'
            requirement='admin'
            requireAllTeams={true}
            renderUnauthorized={false}
          >
            <FormField
              control={control}
              name='target'
              render={({ field }) => (
                <FieldWrapper
                  layout='stacked'
                  label={t('news.target.label')}
                  className='w-40'
                >
                  <Select
                    value={field.value || 'guild'}
                    setValue={(value) =>
                      setValue('target', value as 'team' | 'guild', {
                        shouldDirty: true,
                      })
                    }
                    options={[
                      { value: 'team', label: t('news.target.team') },
                      { value: 'guild', label: t('news.target.guild') },
                    ]}
                  />
                </FieldWrapper>
              )}
            />
          </ProtectedContent>
        )}
      </FormGroup>

      <div>
        <label className='text-sm font-medium mb-2 block'>
          {t(`${i18nPrefix}.${entityType}.mainImageUrl.label`)}
        </label>
        <Tabs
          value={imageTab}
          onValueChange={(v) => setImageTab(v as 'url' | 'upload')}
        >
          <TabsList>
            <TabsTrigger value='url'>{t('news.image-url')}</TabsTrigger>
            <TabsTrigger value='upload'>{t('news.image-upload')}</TabsTrigger>
          </TabsList>
          <TabsContent value='url'>
            <FormField
              control={control}
              name='mainImageUrl'
              render={({ field }) => (
                <Input
                  placeholder={t(
                    `${i18nPrefix}.${entityType}.mainImageUrl.placeholder`,
                  )}
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
          </TabsContent>
          <TabsContent value='upload'>
            <FileUpload
              value={imageFiles}
              onValueChange={handleMainImageUpload}
              onFileReject={(file, message) =>
                setRejection({ fileName: file.name, message })
              }
              accept='image/*'
              maxFiles={1}
              maxSize={5 * 1024 * 1024}
              className='w-full'
            >
              <FileUploadList>
                {imageFiles.map((file) => (
                  <FileUploadItem
                    key={file.name + file.size}
                    value={file}
                    className='border-primary/50'
                  >
                    <FileUploadItemPreview>
                      <ImageIcon className='h-4 w-4' />
                    </FileUploadItemPreview>
                    <FileUploadItemMetadata />
                    <FileUploadItemDelete asChild>
                      <Button
                        variant='ghost'
                        size='icon'
                        className='h-7 w-7 shrink-0'
                        onClick={() => {
                          setImageFiles([])
                          setValue('mainImageUrl', null, { shouldDirty: true })
                        }}
                      >
                        <XIcon className='h-4 w-4' />
                      </Button>
                    </FileUploadItemDelete>
                  </FileUploadItem>
                ))}
              </FileUploadList>

              {imageFiles.length === 0 && (
                <FileUploadDropzone className='min-h-[100px] cursor-pointer bg-card-nested hover:bg-card-nested/70'>
                  <div className='flex flex-col items-center gap-2 text-center'>
                    <UploadIcon className='h-8 w-8 text-muted-foreground' />
                    <div className='text-sm text-muted-foreground'>
                      <span className='font-semibold'>Click to upload</span> or
                      drag and drop
                    </div>
                    <div className='text-xs text-muted-foreground'>
                      Images only, up to 5MB
                    </div>
                  </div>
                </FileUploadDropzone>
              )}
            </FileUpload>
            {rejection && (
              <Alert variant='destructive' className='mt-3'>
                <AlertTitle>Upload rejected</AlertTitle>
                <AlertDescription>
                  {rejection.fileName}: {rejection.message}
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <FormField
        control={control}
        name='content'
        render={({ field }) => (
          <FieldWrapper
            layout='stacked'
            label={t(`${i18nPrefix}.${entityType}.content.label`)}
          >
            <MinimalTiptapEditor
              value={field.value}
              onChange={(value) =>
                setValue('content', value, { shouldDirty: true })
              }
              output='html'
              placeholder={t(`${i18nPrefix}.${entityType}.content.placeholder`)}
              uploader={uploadFile}
              className='min-h-[300px] bg-card-nested'
            />
          </FieldWrapper>
        )}
      />

      {showTags && (
        <FormField
          control={control}
          name='tags'
          render={({ field }) => {
            const selected: string[] = field.value ?? []
            const toggle = (id: string) =>
              setValue(
                'tags',
                selected.includes(id)
                  ? selected.filter((tag) => tag !== id)
                  : [...selected, id],
                { shouldDirty: true },
              )

            return (
              <FieldWrapper layout='stacked' label={t('news.tags.label')}>
                <div className='flex flex-wrap gap-1.5'>
                  {NEWS_TAGS.map((tag) => {
                    const isSelected = selected.includes(tag.id)
                    return (
                      <button
                        key={tag.id}
                        type='button'
                        onClick={() => toggle(tag.id)}
                      >
                        <NewsTagBadge
                          tag={tag.id}
                          className={cn(
                            'cursor-pointer',
                            !isSelected && 'opacity-40',
                          )}
                        />
                      </button>
                    )
                  })}
                </div>
              </FieldWrapper>
            )
          }}
        />
      )}
    </div>
  )
}

const NewsForm = ({
  entityType = 'news_item',
  parentEntityType,
  parentId,
  existingItem,
  isPending,
  listQueryKey,
  uploadFile,
  showTargetSelector,
  showTags = false,
  defaultTarget,
  formPermission,
  canManage,
  guildNewsQueryKey,
}: {
  entityType?: string
  parentEntityType?: NewsParentEntityType
  parentId?: number
  existingItem: wowaudit.NewsItem | wowaudit.GlobalNewsItem | undefined
  isPending: boolean
  listQueryKey: readonly unknown[]
  guildNewsQueryKey?: readonly unknown[]
  uploadFile: (file: File) => Promise<string>
  showTargetSelector: boolean
  showTags?: boolean
  defaultTarget?: 'team' | 'guild'
  formPermission?: (item: NewsFormItem) => NewsPermission
  canManage?: boolean
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id } = useParams()
  const isEditing = !!id

  if (isEditing && isPending) return null

  const defaultItem: NewsFormItem = {
    title: '',
    content: '',
    published: false,
    mainImageUrl: undefined,
    target: defaultTarget,
    tags: [],
  }

  const item =
    isEditing && existingItem
      ? {
          ...existingItem,
          target: showTargetSelector
            ? 'newsableType' in existingItem &&
              existingItem.newsableType === 'Guild'
              ? ('guild' as const)
              : ('team' as const)
            : undefined,
        }
      : defaultItem
  const isDraft = isEditing && !item.published

  const handleBack = () => {
    navigate('..')
  }

  const permission = formPermission?.(item)

  const form = (
    <Card className='max-w-4xl'>
      <h2 className='flex items-center gap-4 justify-between'>
        {isEditing ? t('news.edit-post') : t('news.new-post')}
        <Button
          key='back-to-list'
          size='sm'
          variant='outline'
          type='button'
          onClick={handleBack}
        >
          Back to news
        </Button>
      </h2>
      <FormWrapper<NewsFormItem>
        entityType={entityType}
        fieldEntityType='news_item'
        parentEntityType={parentEntityType}
        parentId={parentId}
        formSchema={formSchema}
        transformValues={(values) => {
          if (showTargetSelector) return values

          const { target: _target, ...guildValues } = values
          return guildValues
        }}
        onSave={(_form, savedItem) => {
          queryClient.invalidateQueries({ queryKey: listQueryKey })

          if (entityType === 'global_news_item') {
            queryClient.invalidateQueries({ queryKey: ['globalNewsItems'] })
            queryClient.invalidateQueries({ queryKey: ['globalNewsItem'] })
            handleBack()
            return
          }

          const isGuildNews =
            parentEntityType === 'guild' ||
            item.target === 'guild' ||
            ('newsableType' in savedItem && savedItem.newsableType === 'Guild')
          const isTeamNews =
            parentEntityType === 'team' &&
            !('newsableType' in savedItem && savedItem.newsableType === 'Guild')

          if (isTeamNews) {
            queryClient.invalidateQueries({ queryKey: ['overviewData'] })
          }

          if (isGuildNews) {
            queryClient.invalidateQueries({ queryKey: ['newsItems'] })
            queryClient.invalidateQueries({ queryKey: ['guildOverviewData'] })
            if (guildNewsQueryKey) {
              queryClient.invalidateQueries({ queryKey: guildNewsQueryKey })
            }
          }
          handleBack()
        }}
        allowDelete={isEditing}
        entity={item}
        i18nPrefix='news.form-fields'
        submitButtons={({ isPending, isDeleting, setValue }) => (
          <>
            <Button
              type='submit'
              variant='outline'
              size='sm'
              disabled={isDeleting}
              loading={isPending}
              onClick={() => setValue('published', false)}
            >
              {isEditing && !isDraft
                ? t('news.unpublish')
                : t('news.save-as-draft')}
            </Button>
            <Button
              type='submit'
              size='sm'
              disabled={isDeleting}
              loading={isPending}
              onClick={() => setValue('published', true)}
            >
              {isEditing && !isDraft
                ? t('shared.save-changes')
                : t('news.publish')}
            </Button>
          </>
        )}
      >
        <NewsFormContent
          uploadFile={uploadFile}
          showTargetSelector={showTargetSelector}
          showTags={showTags}
        />
      </FormWrapper>
    </Card>
  )

  if (canManage !== undefined) {
    return canManage ? form : null
  }

  if (permission) {
    return (
      <ProtectedContent
        section={permission.section}
        requirement={permission.requirement}
        requireAllTeams={permission.requireAllTeams}
      >
        {form}
      </ProtectedContent>
    )
  }

  return form
}

export default NewsForm
